/**
 * Power / thermal governor (docs/PERF.md "Thermal"). One place that knows whether the app should spend energy on
 * ambience:
 *   - hidden:   app backgrounded / tab hidden (visibilitychange + Capacitor appStateChange) → music off, WebAudio
 *               suspended, JS animation loops stop (they check `ambientAllowed()` / `isHidden()`).
 *   - low:      battery saver = Settings "Battery saver" On, or Auto and any of: a low-end device (deviceMemory ≤ 3 GB,
 *               or ≤ 4 cores outside iOS), a low battery (< 20 %, not charging; Battery API where available), or
 *               sustained slow frames (two 2 s samples under 35 fps) → ambient CSS loops stop after one cycle
 *               (`html[data-power='low']`), no world music, no roaming creatures, lighter canvases (DPR ≤ 1.5).
 * Exposed as `<html data-power="low|normal" data-hidden="true|false">` for CSS.
 */
import { useSyncExternalStore } from 'react';

export type BatterySaver = 'auto' | 'on' | 'off';

const nav = (typeof navigator !== 'undefined' ? navigator : {}) as Navigator & { deviceMemory?: number; getBattery?: () => Promise<BatteryLike> };
interface BatteryLike extends EventTarget {
  level: number;
  charging: boolean;
}
const isIOS = typeof navigator !== 'undefined' && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1));

/** Low-end device: little memory or few cores (iOS reports a capped core count, so only memory counts there). */
export const LOW_END = (nav.deviceMemory !== undefined && nav.deviceMemory <= 3) || (!isIOS && (nav.hardwareConcurrency ?? 8) <= 4);

const st = {
  setting: 'auto' as BatterySaver,
  hidden: typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
  lowBattery: false,
  slowFrames: false,
};
export type PowerReason = 'setting' | 'lowEnd' | 'battery' | 'slowFrames';

/** Why battery saver is on (empty = off). */
export function powerReasons(): PowerReason[] {
  if (st.setting === 'off') return [];
  if (st.setting === 'on') return ['setting'];
  const r: PowerReason[] = [];
  if (LOW_END) r.push('lowEnd');
  if (st.lowBattery) r.push('battery');
  if (st.slowFrames) r.push('slowFrames');
  return r;
}
export const isLowPower = () => powerReasons().length > 0;
export const isHidden = () => st.hidden;
/** Decorative motion / sound may run now (visible and not in battery saver). */
export const ambientAllowed = () => !st.hidden && !isLowPower();

const listeners = new Set<() => void>();
let snap = '';
function emit() {
  const low = isLowPower();
  const key = `${low}|${st.hidden}|${powerReasons().join(',')}`;
  if (key === snap) return;
  snap = key;
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.power = low ? 'low' : 'normal';
    document.documentElement.dataset.hidden = String(st.hidden);
  }
  listeners.forEach((l) => l());
}
export const onPowerChange = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));

/** React: re-render when battery saver / visibility changes. */
export function usePower(): { low: boolean; hidden: boolean; ambient: boolean } {
  const key = useSyncExternalStore(onPowerChange, () => snap, () => snap);
  return { low: key.startsWith('true'), hidden: key.split('|')[1] === 'true', ambient: ambientAllowed() };
}

export function setBatterySaver(s: BatterySaver | undefined) {
  st.setting = s ?? 'auto';
  emit();
}

/** Canvas backing-store density: ≤ 2 on phones, ≤ 1.5 in battery saver / low-end; tablets ≤ 2. */
export function canvasDpr(): number {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio || 1;
  return Math.min(dpr, LOW_END || isLowPower() ? 1.5 : 2);
}

/* ------------------------------------------------ wiring ------------------------------------------------ */

let started = false;
/** Start listening (once, at app start). */
export function startPowerGovernor() {
  if (started || typeof document === 'undefined') return;
  started = true;
  const setHidden = (h: boolean) => {
    st.hidden = h;
    emit();
  };
  document.addEventListener('visibilitychange', () => setHidden(document.visibilityState === 'hidden'));
  // Capacitor: the WebView is not always told when the app goes to the background.
  void import('@capacitor/core')
    .then(({ Capacitor }) => (Capacitor.isNativePlatform() ? import('@capacitor/app') : null))
    .then((m) => m?.App.addListener('appStateChange', (s) => setHidden(!s.isActive)))
    .catch(() => undefined);
  // Battery API (Chrome / Android WebView; not Safari).
  nav
    .getBattery?.()
    .then((b) => {
      const upd = () => {
        st.lowBattery = b.level < 0.2 && !b.charging;
        emit();
      };
      upd();
      b.addEventListener('levelchange', upd);
      b.addEventListener('chargingchange', upd);
    })
    .catch(() => undefined);
  // Sustained slow frames: sample 2 s of rAF once a minute while visible (cheap; no loop in between).
  let slow = 0;
  const sample = () => {
    if (st.hidden) return;
    let n = 0;
    const t0 = performance.now();
    const f = () => {
      n++;
      if (performance.now() - t0 < 2000) requestAnimationFrame(f);
      else {
        const fps = (n * 1000) / (performance.now() - t0);
        slow = fps < 35 ? slow + 1 : 0;
        if (slow >= 2 && !st.slowFrames) {
          st.slowFrames = true;
          emit();
        }
      }
    };
    requestAnimationFrame(f);
  };
  setInterval(sample, 60_000);
  setTimeout(sample, 8_000);
  emit();
}
emit();
